/* eslint-disable react/display-name */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Layout,
    Menu,
    Form,
    Input,
    Button,
    Table,
    Select,
    Popover,
    Modal,
    DatePicker,
    Upload,
    Row,
    Col,
    TreeSelect,
    Checkbox,
    Badge,
} from 'antd';
import Gstyle from './index.module.less';
import styles from './fileManagement.module.less';
import layoutStyles from './sharePageLayout.module.less';
import useTableScrollY from './useTableScrollY';
import editIcon from '@/asset/new/edit.png'; // 引入编辑图标
import editIcon2 from '@/asset/new/edit_1.png'; // 引入编辑图标
import DeleteIcon from '@/asset/new/Delete.png'; // 引入编辑图标
import TerminationIcons from '@/asset/new/Termination-files.png'; // 引入编辑图标
import TerminationIcon from '@/asset/new/Termination-file.png'; // 引入编辑图标
import closeIcon from '@/asset/new/Close.png'; // 引入编辑图标
import {
    knowledgeList,
    knowledgeTree,
    knowledgeFolderAdd,
    knowledgeUpdate,
    knowledgeFolderDelete,
    knowledgeFolderUpdate,
    knowledgeUpload,
    knowledgeAdd,
    knowledgeDelete,
    knowledgeDictionaryList,
    knowledgeTaTagList,
    knowledgeFolderFileCount,
} from '@/api/knowledge';
import { API_ROOT } from '@/env';
import { downloadExportFile } from './downloadExport';
import dayjs from 'dayjs';
const { Sider, Content } = Layout;
const { TextArea } = Input;
const { SubMenu } = Menu;

/** 侧边文件夹菜单：level 0（根节点）不可选，其余节点（含非根父级）可选 */
function isFolderMenuSelectable(level) {
    return level > 0;
}

/**
 * 深度优先取第一个可选节点（level > 0），并返回需展开的父级 id
 */
function getFirstSelectableFolderSelection(nodes, level = 0, ancestorIds = []) {
    if (!Array.isArray(nodes) || nodes.length === 0) {
        return null;
    }
    for (const n of nodes) {
        if (isFolderMenuSelectable(level)) {
            return { fileId: n.id, openKeys: ancestorIds };
        }
        const children = n.children;
        const hasChildren = Array.isArray(children) && children.length > 0;
        if (hasChildren) {
            const nested = getFirstSelectableFolderSelection(
                children,
                level + 1,
                [...ancestorIds, n.id],
            );
            if (nested) {
                return nested;
            }
        }
    }
    return null;
}

/** 在文件夹树中按 id 查找节点 */
function findFolderNodeById(nodes, id) {
    if (!Array.isArray(nodes) || id == null || id === '') {
        return null;
    }
    for (const n of nodes) {
        if (String(n.id) === String(id)) {
            return n;
        }
        const found = findFolderNodeById(n.children, id);
        if (found) {
            return found;
        }
    }
    return null;
}

/** 文件夹树 → TreeSelect 的 treeData（根节点 level 0 不可选） */
function mapTreeToTreeSelectNodes(nodes, level = 0) {
    if (!Array.isArray(nodes) || nodes.length === 0) {
        return [];
    }
    return nodes.map(n => {
        const hasChildren =
            Array.isArray(n.children) && n.children.length > 0;
        return {
            title: `${n.name ?? ''} (${n.validNumber ?? 0})`,
            value: n.id,
            key: String(n.id),
            selectable: isFolderMenuSelectable(level),
            children: hasChildren
                ? mapTreeToTreeSelectNodes(n.children, level + 1)
                : undefined,
        };
    });
}

/** 字典列表 → Select options（展示 dictLabel，提交 dictValue） */
function mapDictListToOptions(list) {
    if (!Array.isArray(list)) {
        return [];
    }
    return list.map(item => ({
        label: item.dictLabel,
        value: item.dictValue,
    }));
}

/** TA Tag 列表 → 多选 options（value 为 id） */
function mapTaTagListToOptions(list, { onlyEnabled = false } = {}) {
    if (!Array.isArray(list)) {
        return [];
    }
    const rows = onlyEnabled ? list.filter(item => item.status === 1) : list;
    return rows.map(item => ({
        label: item.tagName || item.tagType || String(item.id),
        value: item.id,
    }));
}

/** 编辑回填：接口 taTag 可能是 id、逗号分隔字符串或数组 */
function normalizeTaTagFormValue(value) {
    if (value == null || value === '') {
        return undefined;
    }
    if (Array.isArray(value)) {
        return value.map(v => (typeof v === 'number' ? v : Number(v) || v));
    }
    if (typeof value === 'string' && value.includes(',')) {
        return value
            .split(',')
            .map(item => item.trim())
            .filter(Boolean)
            .map(v => {
                const n = Number(v);
                return Number.isNaN(n) ? v : n;
            });
    }
    const single = Number(value);
    return Number.isNaN(single) ? [value] : [single];
}

/** 字节 → MB 展示（上传接口 fileSize 为字节字符串） */
function formatFileSizeMb(fileSize) {
    if (fileSize == null || fileSize === '') {
        return '';
    }
    const mb = Number(fileSize) / 1024 / 1024;
    if (Number.isNaN(mb)) {
        return '';
    }
    return `${mb.toFixed(4)}MB`;
}

/** 上传列表：文件名旁展示大小，仅用于展示 */
function buildUploadFileDisplayName(name, fileSize) {
    if (!name) {
        return name;
    }
    const sizeText = formatFileSizeMb(fileSize);
    return sizeText ? `${name} (${sizeText})` : name;
}

/** 按 BU 拉取 TA Tag 下拉选项；onlyEnabled 为 true 时仅返回启用（status=1） */
async function fetchTaTagOptionsByBu(buId, { onlyEnabled = false } = {}) {
    if (buId == null || buId === '') {
        return [];
    }
    try {
        const res = await knowledgeTaTagList({
            pageNo: 1,
            pageSize: 9999,
            buId,
            searchType: 1,
        });
        if (res.status === 200 && res.data != null) {
            const data = res.data;
            const records = data.records ?? data.list ?? data.rows ?? [];
            return mapTaTagListToOptions(records, { onlyEnabled });
        }
        return [];
    } catch {
        return [];
    }
}

/** 表格展示：接口返回的 dictValue 映射为 dictLabel（与下拉 options 一致） */
function formatDictValueAsLabel(value, options) {
    if (value == null || value === '') {
        return ' ';
    }
    const list = Array.isArray(options) ? options : [];
    const hit = list.find(o => String(o.value) === String(value));
    return hit != null ? hit.label : String(value);
}

function normalizeDictRows(res) {
    if (!res || res.status !== 200 || res.data == null) {
        return [];
    }
    const raw = res.data;
    if (Array.isArray(raw)) {
        return raw;
    }
    return raw.records ?? raw.list ?? raw.rows ?? [];
}

// 动态菜单数据源
const FileManagement = () => {
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [treeMenuData, setTreeMenuData] = useState([]);
    const [openKeys, setOpenKeys] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [tabkey, setTabkey] = useState('valid');
    const [dataSource, setDataSource] = useState([]);
    const [total, setTotal] = useState(0);
    const [current, setCurrent] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [filevisible, setFilevisible] = useState(false);
    const [fileitem, setFileitem] = useState({});
    const [folderNameInput, setFolderNameInput] = useState('');
    const [fileTitle, setFileTitle] = useState('新建文件夹');
    /** 当前展开文件夹操作 Popover 的节点 id，受控以在点击菜单项后关闭气泡 */
    const [folderPopoverVisibleId, setFolderPopoverVisibleId] = useState(null);
    /** 文件夹弹窗：add | edit | delete */
    const [folderModalType, setFolderModalType] = useState('add');
    /** 文件弹窗：add | edit */
    const [fileFormMode, setFileFormMode] = useState('add');
    const [filevisibleType, setFilevisibleType] = useState(false); // folder: 文件夹，file: 文件
    const [loading, setLoading] = useState(false);
    const { tableWrapRef, tableScrollY } = useTableScrollY([
        dataSource.length,
        total,
        pageSize,
        tabkey,
        loading,
    ]);
    /** 文件列表查询条件（点击「查询」后生效） */
    const [listFilters, setListFilters] = useState({});
    /** 全局搜索时 Tab 角标数量（来自 file-count 接口） */
    const [globalSearchFileCounts, setGlobalSearchFileCounts] = useState({
        validNumber: 0,
        invalidNumber: 0,
    });
    //文件夹的增删改
    const [form] = Form.useForm(); // 新增/编辑文件弹窗
    const [searchForm] = Form.useForm(); // 列表筛选表单
    /** 受控上传列表，关闭弹窗时需清空，否则会残留上次已传文件 */
    const [uploadFileList, setUploadFileList] = useState([]);
    /** 字典下拉：BU / ta（适应症）；TA Tag 按 BU 调 knowledgeTaTagList */
    const [dictBuOptions, setDictBuOptions] = useState([]);
    const [dictTaOptions, setDictTaOptions] = useState([]);
    const [searchTaTagOptions, setSearchTaTagOptions] = useState([]);
    const [searchFormBuId, setSearchFormBuId] = useState(undefined);
    const [fileFormTaTagOptions, setFileFormTaTagOptions] = useState([]);
    const [fileFormBuId, setFileFormBuId] = useState(undefined);
    /** 删除 / 失效确认弹窗（样式与 filevisible 文件夹弹窗一致） */
    const [fileConfirmModal, setFileConfirmModal] = useState({
        visible: false,
        mode: null,
        record: null,
        /** 失效时选择的有效期至（不能早于当天） */
        invalidateExpireDate: null,
        /**
         * full=有效文件：仅文案确认，提交当天+status0；dateOnly=失效文件：必选有效期，提交 status1
         */
        invalidateUi: 'full',
    });
    const [errorModalVisible, setErrorModalVisible] = useState(false);
    const [errorModalContent, setErrorModalContent] = useState('');
    // 列表数据
    const fetchData = useCallback(async () => {
        const folderId = selectedKeys[0] || "";
        if (folderId === undefined) {
            setDataSource([]);
            setTotal(0);
            setGlobalSearchFileCounts({ validNumber: 0, invalidNumber: 0 });
            return;
        }
        const isGlobalSearch = !!listFilters.globalSearch;
        const listParams = {
            pageNo: current,
            pageSize: pageSize,
            folderId,
            ...listFilters,
            status: tabkey === 'valid' ? 1 : 0,
            type: isGlobalSearch ? 1 : 0,
        };
        const [res, countRes] = await Promise.all([
            knowledgeList(listParams),
            isGlobalSearch
                ? knowledgeFolderFileCount()
                : Promise.resolve(null),
        ]);
        if (res.status === 200 || res.success === true) {
            const data = res.data;
            setDataSource(data.records);
            setTotal(data.total);
        }
        if (
            countRes &&
            (countRes.status === 200 || countRes.success === true) &&
            countRes.data != null
        ) {
            setGlobalSearchFileCounts({
                validNumber: countRes.data.validNumber ?? 0,
                invalidNumber: countRes.data.invalidNumber ?? 0,
            });
        }
    }, [current, listFilters, pageSize, selectedKeys, tabkey]);

    const handleSearchList = () => {
        const v = searchForm.getFieldsValue();
        const next = {};
        if (v.bu != null && v.bu !== '') {
            next.bu = v.bu;
        }
        if (v.taTag != null && v.taTag !== '') {
            next.taTag = Array.isArray(v.taTag) ? v.taTag : [v.taTag];
        }
        if (v.name && String(v.name).trim()) {
            next.name = String(v.name).trim();
        }
        if (v.approvalCode && String(v.approvalCode).trim()) {
            next.approvalCode = String(v.approvalCode).trim();
        }
        if (v.globalSearch) {
            next.globalSearch = 1;
        }
        setListFilters(next);
        setCurrent(1);
    };

    const handleGlobalSearchChange = useCallback(e => {
        const checked = e.target.checked;
        setListFilters(prev => {
            const next = { ...prev };
            if (checked) {
                next.globalSearch = 1;
            } else {
                delete next.globalSearch;
            }
            return next;
        });
        setCurrent(1);
    }, []);

    const handleResetListSearch = () => {
        searchForm.resetFields();
        setSearchFormBuId(undefined);
        setSearchTaTagOptions([]);
        setListFilters({});
        setCurrent(1);
    };

    const handleSearchBuChange = useCallback(
        buId => {
            setSearchFormBuId(buId);
            searchForm.setFieldsValue({ taTag: undefined });
            fetchTaTagOptionsByBu(buId).then(setSearchTaTagOptions);
        },
        [searchForm],
    );

    /** 选中文件夹：与当前相同则不更新状态，避免重复请求列表 */
    const handleFolderMenuSelect = useCallback(
        key => {
            const nextKey = String(key);
            if (String(selectedKeys[0]) === nextKey) {
                return;
            }
            setSelectedKeys([nextKey]);
            setCurrent(1);
        },
        [selectedKeys],
    );

    //初始化列表
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    useEffect(() => {
        knowledgeTreeData({ selectFirstLeaf: true });
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [buRes, taRes] = await Promise.all([
                    knowledgeDictionaryList({ dictType: 'BU' }),
                    knowledgeDictionaryList({ dictType: 'ta' }),
                ]);
                if (cancelled) {
                    return;
                }
                setDictBuOptions(mapDictListToOptions(normalizeDictRows(buRes)));
                setDictTaOptions(mapDictListToOptions(normalizeDictRows(taRes)));
            } catch {
                if (!cancelled) {
                    setDictBuOptions([]);
                    setDictTaOptions([]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const loadFileFormTaTagOptions = useCallback(async buId => {
        setFileFormTaTagOptions(await fetchTaTagOptionsByBu(buId, { onlyEnabled: true }));
    }, []);

    const resetFileFormTaTagState = useCallback(() => {
        setFileFormBuId(undefined);
        setFileFormTaTagOptions([]);
    }, []);

    const handleFileFormBuChange = useCallback(
        buId => {
            setFileFormBuId(buId);
            form.setFieldsValue({ taTag: undefined });
            loadFileFormTaTagOptions(buId);
        },
        [form, loadFileFormTaTagOptions],
    );

    useEffect(() => {
        if (!filevisibleType) {
            setUploadFileList([]);
            resetFileFormTaTagState();
        }
    }, [filevisibleType, resetFileFormTaTagState]);
    /**
     * 拉取文件夹树。
     * @param {{ selectFirstLeaf?: boolean }} [options] 仅首次进入传 true：默认选中第一个可选节点（跳过根节点）并展开路径；其余刷新只更新树，不改变当前选中项。
     */
    const knowledgeTreeData = async (options = {}) => {
        const { selectFirstLeaf = false } = options;
        const res = await knowledgeTree();
        if (res.status === 200) {
            const data = res.data;
            const list = Array.isArray(data) ? data : [];
            setTreeMenuData(list);
            if (selectFirstLeaf) {
                const sel = getFirstSelectableFolderSelection(list);
                if (sel) {
                    setSelectedKeys([String(sel.fileId)]);
                    setOpenKeys(sel.openKeys.map(String));
                } else {
                    setSelectedKeys([]);
                    setOpenKeys([]);
                }
            }
        }
    };
    const renderFolderPopoverContent = item => (
        <div className={styles.dropdownMenu}>
            <div
                key="create"
                onClick={() => {
                    setFileitem(item);
                    setFilevisible(true);
                    setFileTitle('新建子文件夹');
                    setFolderModalType('add');
                    setFolderNameInput('');
                    setFolderPopoverVisibleId(null);
                }}
            >
                新建子文件夹
            </div>
            <div
                key="edit"
                onClick={() => {
                    setFileitem(item);
                    setFilevisible(true);
                    setFileTitle('编辑文件夹');
                    setFolderModalType('edit');
                    setFolderNameInput(item.name || '');
                    setFolderPopoverVisibleId(null);
                }}
            >
                编辑文件夹
            </div>
            <div
                key="delete"
                onClick={() => {
                    setFileitem(item);
                    setFilevisible(true);
                    setFileTitle('删除文件夹');
                    setFolderModalType('delete');
                    setFolderPopoverVisibleId(null);
                }}
            >
                删除文件夹
            </div>
        </div>
    );

    // 渲染带图标的 Label
    const renderLabelWithIcon = item => (
        <div className="menu-label-wrapper">
            <span
                className="menu-label-text"
                style={{
                    maxWidth: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                }}
                title={item.name + '(' + (item.validNumber ?? 0) + ')'}
            >
                {item.name}({item.validNumber ?? 0})
            </span>

            {isEditing && (
                <span
                    className="menu-edit-icon-wrapper"
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                >
                    <Popover
                        visible={String(folderPopoverVisibleId) === String(item.id)}
                        onVisibleChange={visible => {
                            if (visible) {
                                setFileitem(item);
                                setFolderPopoverVisibleId(item.id);
                            } else {
                                setFolderPopoverVisibleId(null);
                            }
                        }}
                        content={renderFolderPopoverContent(item)}
                        trigger="click"
                        placement="right"
                    >
                        <img
                            src={
                                String(folderPopoverVisibleId) === String(item.id)
                                    ? editIcon2
                                    : editIcon
                            }
                            alt="编辑"
                            className="menu-edit-icon"
                        />
                    </Popover>
                </span>
            )}
        </div>
    );
    // 动态递归渲染菜单（level 0 根节点不可选）
    const renderMenu = (data, level = 0) => {
        return data.map(item => {
            const canSelect = isFolderMenuSelectable(level);
            if (item.children && item.children.length > 0) {
                const parentRowSelected =
                    canSelect && String(selectedKeys[0]) === String(item.id);
                return (
                    <SubMenu
                        key={String(item.id)}
                        className={parentRowSelected ? 'submenu-parent-selected' : undefined}
                        title={renderLabelWithIcon(item)}
                        onTitleClick={
                            canSelect
                                ? ({ key }) => handleFolderMenuSelect(key)
                                : undefined
                        }
                    >
                        {renderMenu(item.children, level + 1)}
                    </SubMenu>
                );
            }
            return (
                <Menu.Item key={String(item.id)} disabled={!canSelect}>
                    {renderLabelWithIcon(item)}
                </Menu.Item>
            );
        });
    };
    const onOpenChange = keys => {
        setOpenKeys(keys);
    };

    const openEditFile = useCallback(
        async record => {
            setFileFormMode('edit');
            setUploadFileList([]);
            form.resetFields();
            resetFileFormTaTagState();
            const bu = record.bu;
            setFileFormBuId(bu);
            await loadFileFormTaTagOptions(bu);
            form.setFieldsValue({
                id: record.id,
                folderId: record.folderId,
                expirationDate: record.expirationDate
                    ? dayjs(record.expirationDate)
                    : undefined,
                approvalCode: record.approvalCode,
                bu,
                taTag: normalizeTaTagFormValue(record.taTag ?? record.ta_tag),
                fileId: record.fileId,
                name: record.name,
                descInfo: record.descInfo,
                status: record.status,
            });
            if (record.name) {
                setUploadFileList([
                    {
                        uid: `-${record.fileId ?? record.id}`,
                        originName: record.name,
                        fileSize: record.fileSize,
                        name: record.name + '(' + record.fileSize + ')',
                        status: 'done',
                    },
                ]);
            }
            setFilevisibleType(true);
        },
        [form, loadFileFormTaTagOptions, resetFileFormTaTagState]
    );

    const closeFileConfirmModal = () => {
        setFileConfirmModal({
            visible: false,
            mode: null,
            record: null,
            invalidateExpireDate: null,
            invalidateUi: 'full',
        });
    };

    const onConfirmFileActionModal = async () => {
        const { mode, record, invalidateExpireDate, invalidateUi } = fileConfirmModal;
        if (!record || !mode) {
            return;
        }

        if (mode === 'delete') {
            setLoading(true);
            try {
                const res = await knowledgeDelete(record.id);
                if (res.status === 200) {
                    closeFileConfirmModal();
                    fetchData();
                    await knowledgeTreeData();
                }
            } finally {
                setLoading(false);
            }
            return;
        }

        if (mode === 'invalidate') {
            /** 失效文件列表：必选有效期至，提交 status=1（恢复有效） */
            if (invalidateUi === 'dateOnly') {
                if (!invalidateExpireDate || !dayjs(invalidateExpireDate).isValid()) {
                    Modal.warning({ title: '提示', content: '请选择有效期至' });
                    return;
                }
                const picked = dayjs(invalidateExpireDate).startOf('day');
                const todayStart = dayjs().startOf('day');
                if (picked.isBefore(todayStart)) {
                    Modal.warning({
                        title: '提示',
                        content: '有效期至不能早于当前日期',
                    });
                    return;
                }
                const expirationDate = picked.format('YYYY-MM-DD');
                setLoading(true);
                try {
                    const res = await knowledgeUpdate({
                        ...record,
                        id: record.id,
                        expirationDate,
                        status: 1,
                    });
                    if (res.status === 200) {
                        closeFileConfirmModal();
                        fetchData();
                        await knowledgeTreeData();
                    }
                } finally {
                    setLoading(false);
                }
                return;
            }
            /** 有效文件列表：仅文案确认；默认当天日期 + status=0 */
            const expirationDate = dayjs().format('YYYY-MM-DD');
            setLoading(true);
            try {
                const res = await knowledgeUpdate({
                    ...record,
                    id: record.id,
                    status: 0,
                    expirationDate,
                });
                if (res.status === 200) {
                    closeFileConfirmModal();
                    fetchData();
                    await knowledgeTreeData();
                }
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDeleteFile = useCallback(record => {
        setFileConfirmModal({
            visible: true,
            mode: 'delete',
            record,
            invalidateExpireDate: null,
            invalidateUi: 'full',
        });
    }, []);

    /** 有效文件列表：仅确认文案；确定后当天日期 + status=0 */
    const handleInvalidateFromValidTab = useCallback(record => {
        setFileConfirmModal({
            visible: true,
            mode: 'invalidate',
            invalidateUi: 'full',
            record,
            invalidateExpireDate: null,
        });
    }, []);

    /** 失效文件列表：选择有效期至；确定后 status=1 */
    const handleExpirationFromInvalidTab = useCallback(record => {
        setFileConfirmModal({
            visible: true,
            mode: 'invalidate',
            invalidateUi: 'dateOnly',
            record,
            invalidateExpireDate: record.expirationDate
                ? dayjs(record.expirationDate)
                : dayjs(),
        });
    }, []);

    const handleDownloadFile = useCallback(record => {
        if (!record?.fileId) {
            return;
        }
        downloadExportFile({
            url: `${API_ROOT}/knowledge/file/download`,
            method: 'get',
            params: { fileId: record.fileId },
            fileName: record.name || 'download',
            errorMessage: '文件下载失败',
        });
    }, []);

    // 表格列配置
    const columns = useMemo(
        () => [
            {
                title: 'BU',
                dataIndex: 'bu',
                key: 'bu',
                width: 100,
                render: v => formatDictValueAsLabel(v, dictBuOptions),
            },
            {
                title: 'TA TAG',
                dataIndex: 'taTagNames',
                key: 'taTagNames',
                width: 160,
                render: text => {
                    if (!text) return ' ';
                    return text.join(',');
                },
            },
            {
                title: '文件名称',
                dataIndex: 'name',
                key: 'name',
                width: 280,
                ellipsis: true,
                render: (text, record) => {
                    if (!text) {
                        return ' ';
                    }
                    if (!record.fileId) {
                        return text;
                    }
                    return (
                        <a
                            title="点击下载到本地"
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDownloadFile(record);
                            }}
                        >
                            {text}
                        </a>
                    );
                },
            },
            {
                title: '文件大小',
                dataIndex: 'fileSize',
                key: 'fileSize',
                width: 120,
            },
            { title: '文件审批号', dataIndex: 'approvalCode', key: 'approvalCode', width: 160 },
            {
                title: '上传时间',
                dataIndex: 'createTime',
                key: 'createTime',
                width: 180,
                render: text => {
                    if (!text) return ' ';
                    return dayjs(text).format('YYYY-MM-DD HH:mm:ss');
                },
            },
            { title: '文件夹', dataIndex: 'folderName', key: 'folderName', width: 120, ellipsis: true },
            {
                title: tabkey === 'valid' ? '有效期至' : '失效期',
                dataIndex: 'expirationDate',
                key: 'expirationDate',
                width: 150,
                render: text => {
                    if (!text) return ' ';
                    return dayjs(text).format('YYYY-MM-DD');
                },
            },
            {
                title: '操作',
                key: 'action',
                width: 120,
                fixed: 'right',
                onHeaderCell: () => ({
                    style: {
                        background: '#f1f3ff',
                    },
                }),
                onCell: (_record, rowIndex) => ({
                    style: {
                        // 必须实色，rgba/透明会导致横向滚动时下层文字透出
                        background:
                            rowIndex != null && (rowIndex + 1) % 2 === 0 ? '#f3f8fc' : '#ffffff',
                    },
                }),
                render: (_text, record) => (
                    <div className={styles.actionIcons}>
                        {tabkey === 'valid' && (
                            <img
                                src={editIcon}
                                alt="编辑"
                                title="编辑"
                                style={{ cursor: 'pointer' }}
                                onClick={e => {
                                    e.stopPropagation();
                                    openEditFile(record);
                                }}
                            />
                        )}
                        <img
                            src={DeleteIcon}
                            alt="删除"
                            title="删除"
                            style={{ cursor: 'pointer' }}
                            onClick={e => {
                                e.stopPropagation();
                                handleDeleteFile(record);
                            }}
                        />
                        <img
                            src={tabkey === 'valid' ? TerminationIcons : TerminationIcon}
                            alt={tabkey === 'valid' ? '失效' : '有效期'}
                            title={
                                tabkey === 'valid'
                                    ? '设为失效'
                                    : '修改有效期至'
                            }
                            style={{ cursor: 'pointer' }}
                            onClick={e => {
                                e.stopPropagation();
                                if (tabkey === 'valid') {
                                    handleInvalidateFromValidTab(record);
                                } else {
                                    handleExpirationFromInvalidTab(record);
                                }
                            }}
                        />
                    </div>
                ),
            },
        ],
        [
            dictBuOptions,
            dictTaOptions,
            handleDeleteFile,
            handleDownloadFile,
            handleExpirationFromInvalidTab,
            handleInvalidateFromValidTab,
            openEditFile,
            tabkey,
        ]
    );
    const onConfirmFolderModal = async () => {
        if (folderModalType === 'delete') {
            if (!fileitem.id) {
                return;
            }
            setLoading(true);
            try {
                const res = await knowledgeFolderDelete(fileitem.id);
                if (res.status === 200) {
                    setFilevisible(false);
                    await knowledgeTreeData();
                }
            } finally {
                setLoading(false);
            }
            return;
        }
        const name = (folderNameInput || '').trim();
        if (!name) {
            Modal.warning({ title: '提示', content: '请输入文件夹名称' });
            return;
        }
        setLoading(true);
        try {
            let res;
            if (folderModalType === 'add') {
                res = await knowledgeFolderAdd({ parentId: fileitem.id, name });
            } else if (folderModalType === 'edit') {
                res = await knowledgeFolderUpdate({ id: fileitem.id, name });
            }
            if (res && res.status === 200) {
                setFilevisible(false);
                await knowledgeTreeData();
            } else {
                setErrorModalVisible(true);
                setErrorModalContent(res.message || '操作失败，请重试！');
            }
        } finally {
            setLoading(false);
        }
    };
    const onConfirmFileModal = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            const expirationDate = values.expirationDate
                ? dayjs(values.expirationDate).format('YYYY-MM-DD')
                : undefined;
            const todayStart = dayjs().startOf('day');
            const status =
                expirationDate &&
                    dayjs(expirationDate).startOf('day').isBefore(todayStart)
                    ? 0
                    : 1;
            const body = {
                folderId: values.folderId,
                name: values.name,
                taTag: values.taTag,
                expirationDate,
                approvalCode: values.approvalCode,
                bu: values.bu,
                fileId: values.fileId,
                descInfo: values.descInfo,
                status,
            };
            let res;

            if (fileFormMode === 'edit' && values.id != null && values.id !== '') {
                body.id = values.id;
                res = await knowledgeUpdate(body);
            } else {
                res = await knowledgeAdd(body);
            }
            if (res && res.status === 200) {
                setUploadFileList([]);
                setFilevisibleType(false);
                form.resetFields();
                resetFileFormTaTagState();
                fetchData();
                knowledgeTreeData();
                if (fileFormMode === 'add') {
                    setErrorModalVisible(true);
                    setErrorModalContent('文件已新增，请查看！');
                } else {
                    setErrorModalVisible(true);
                    setErrorModalContent('编辑成功，请查看！');
                }
            } else {
                setErrorModalVisible(true);
                setErrorModalContent(res.message || '操作失败，请重试！');
            }
        } catch (e) {
            if (e && e.errorFields) {
                return;
            }
        } finally {
            setLoading(false);
        }
    };

    const folderTreeSelectData = useMemo(
        () => mapTreeToTreeSelectNodes(treeMenuData),
        [treeMenuData]
    );

    const selectedFolderCounts = useMemo(() => {
        if (listFilters.globalSearch) {
            return globalSearchFileCounts;
        }
        const folderId = selectedKeys[0];
        if (!folderId) {
            return { validNumber: 0, invalidNumber: 0 };
        }
        const node = findFolderNodeById(treeMenuData, folderId);
        return {
            validNumber: node?.validNumber ?? 0,
            invalidNumber: node?.invalidNumber ?? 0,
        };
    }, [globalSearchFileCounts, listFilters.globalSearch, selectedKeys, treeMenuData]);

    const onAdd = () => {
        setFileFormMode('add');
        setUploadFileList([]);
        form.resetFields();
        resetFileFormTaTagState();
        const currentFolderId = selectedKeys[0];
        if (currentFolderId != null && currentFolderId !== '') {
            form.setFieldsValue({
                folderId: currentFolderId,
            });
        }
        setFilevisibleType(true);
    };
    const closeErrorModal = () => {
        setErrorModalVisible(false);
        setErrorModalContent('');
    };

    return (
        <>
            {/* 自定义样式覆盖，精准控制菜单颜色规范及布局 */}
            <style>{`
	                /* 最外层父级标题：颜色 #071D49 加粗 */
	                .custom-menu > .ant-menu-submenu > .ant-menu-submenu-title {
	                    color: #071D49 !important;
	                    font-weight: bold !important;
	                }
	                /* 未选中的子菜单 Item：颜色 rgba(7, 29, 73, 0.6) */
	                .custom-menu .ant-menu-item {
	                    color: rgba(7, 29, 73, 0.6) !important;
	                }
                        .custom-menu .ant-menu-submenu-inline {
	                    color: rgba(7, 29, 73, 0.6) !important;
	                }
	                /* 最外层无子节点的 Item (如: XX用途)：颜色 #071D49 加粗 */
	                .custom-menu > .ant-menu-item-only-child {
	                    color: #071D49 !important;
	                    font-weight: bold !important;
	                }
	                /* 选中的子菜单 Item：颜色 #071D49，背景色 rgba(0, 102, 245, 0.15) */
	                .custom-menu .ant-menu-item-selected {
	                    color: #071D49 !important;
	                    background-color: rgba(0, 102, 245, 0.15) !important;
	                }
	                /* 修复 antd 默认选中时的左侧高亮条 */
	                .custom-menu .ant-menu-item-selected::after {
	                    border-right: 3px solid #071D49 !important;
	                    left: 0 !important;
	                    right: auto;
	                }
                         .custom-menu .ant-menu-inline .ant-menu-item::after {
                            border-right: 3px solid #071D49 !important;
                            left: 0 !important;
                            right: auto;
                        }
	                .ant-menu-submenu-arrow {
	                    color: #071D49 !important;
	                }
	                /* 覆盖 hover 时子菜单箭头图标的颜色 */
	                .custom-menu .ant-menu-submenu-title:hover .ant-menu-submenu-arrow::before,
	                .custom-menu .ant-menu-submenu-title:hover .ant-menu-submenu-arrow::after {
	                    background: #071D49 !important;
	                }
	                /* 允许选父级时：整行标题栏（含右侧箭头）同一选中底色 */
	                .custom-menu .ant-menu-submenu.submenu-parent-selected > .ant-menu-submenu-title {
	                    color: #071D49 !important;
	                    font-weight: bold !important;
	                    background-color: rgba(0, 102, 245, 0.15) !important;
	                    border-radius: 2px;
	                    box-shadow: inset 3px 0 0 #071D49;
	                }
	                .ant-menu-submenu,.ant-menu-sub.ant-menu-inline{
	                    background: #ffffff !important;
	                }
	                /* --- Label 与 图标的排版控制 --- */
	                .custom-menu .ant-menu-title-content {
	                    display: inline-flex;
	                    align-items: center;
	                    flex: 1;
	                }
	                .menu-label-wrapper {
	                    display: flex;
	                    justify-content: space-between;
	                    align-items: center;
	                    width: 100%;
	                }
                    .menu-edit-icon-wrapper {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        line-height: 1;
                    }
	                .menu-edit-icon {
	                    width: 14px;
	                    height: 14px;
	                    margin-left: 8px;
	                    opacity: 0.6;
	                    transition: opacity 0.3s;
	                    cursor: pointer;
                        position: absolute;
                        right: 34px;
	                }
                    .ant-popover-placement-right{
                       left:176px  !important;
                    }
                    .ant-popover-inner-content {
                        padding: 10px;
                    }
	            `}</style>
            <Layout style={{ height: '100vh', background: '#EBEDF9' }}>
                <Sider width={180} style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}>
                    <div className={styles.menuEdit} onClick={() => setIsEditing(!isEditing)}>
                        {isEditing ? '完成编辑' : '编辑文件'}
                        <img src={editIcon} alt="编辑" />
                    </div>
                    <Menu
                        className="custom-menu"
                        mode="inline"
                        selectedKeys={selectedKeys}
                        openKeys={openKeys}
                        onOpenChange={onOpenChange}
                        onSelect={({ key }) => handleFolderMenuSelect(key)}
                        style={{
                            height: 'calc(100vh - 56px)',
                            borderRight: 0,
                            overflowY: 'auto',
                            background: '#ffffff',
                        }}
                        inlineIndent={12}
                    >
                        {renderMenu(treeMenuData)}
                    </Menu>
                </Sider>
                <Content className={`${Gstyle.content} ${layoutStyles.pageContent}`}>
                    <div className={Gstyle.contentHeader} style={{ flexShrink: 0 }}>
                        文件列表
                    </div>
                    <Form
                        form={searchForm}
                        layout="inline"
                        className={layoutStyles.inlineFilterForm}
                        style={{
                            marginBottom: 8,
                            flexShrink: 0,
                        }}
                    >
                        <Form.Item name="bu" label="BU">
                            <Select
                                placeholder="请选择BU"
                                options={dictBuOptions}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                style={{ width: 170 }}
                                onChange={handleSearchBuChange}
                            />
                        </Form.Item>
                        <Form.Item name="taTag" label="TA TAG">
                            <Select
                                placeholder={searchFormBuId ? '请选择TA' : '请先选择BU'}
                                options={searchTaTagOptions}
                                disabled={!searchFormBuId}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                style={{ width: 170 }}
                            />
                        </Form.Item>
                        <Form.Item name="name" label="文件名称">
                            <Input placeholder="请输入" allowClear style={{ width: 170 }} />
                        </Form.Item>
                        <Form.Item name="approvalCode" label="文件审批号">
                            <Input placeholder="请输入" allowClear style={{ width: 170 }} />
                        </Form.Item>
                        <Form.Item name="globalSearch" valuePropName="checked">
                            <Checkbox onChange={handleGlobalSearchChange}>全局搜索</Checkbox>
                        </Form.Item>
                        <div className={styles.searchFormActions}>
                            <Form.Item>
                                <Button type="primary" onClick={handleSearchList}>
                                    查询
                                </Button>
                                <Button
                                    type="primary"
                                    style={{ marginLeft: 30 }}
                                    onClick={handleResetListSearch}
                                >
                                    重置
                                </Button>
                                <Button type="primary" style={{ marginLeft: 30 }} onClick={onAdd}>
                                    新增
                                </Button>
                            </Form.Item>
                        </div>
                    </Form>
                    <Menu
                        mode="horizontal"
                        className={styles.tableMenu}
                        style={{ flexShrink: 0 }}
                        triggerSubMenuAction="click"
                        selectedKeys={[tabkey]}
                        onClick={e => setTabkey(e.key)}
                    >
                        <Menu.Item
                            key="valid"
                            className={`${styles.meunitem} ${tabkey === 'invalid' ? styles.meunactiv : ''
                                }`}
                        >
                            <Badge
                                count={selectedFolderCounts.validNumber}
                                overflowCount={99999}
                                showZero
                                className={styles.tabBadge}
                            >
                                <span className={styles.tabMenuLabel}>有效文件</span>
                            </Badge>
                        </Menu.Item>
                        <Menu.Item
                            key="invalid"
                            className={`${styles.meunitem} ${tabkey === 'valid' ? styles.meunactiv : ''
                                }`}
                        >
                            <Badge
                                count={selectedFolderCounts.invalidNumber}
                                overflowCount={99999}
                                showZero
                                className={styles.tabBadge}
                            >
                                <span className={styles.tabMenuLabel}>失效文件</span>
                            </Badge>
                        </Menu.Item>
                    </Menu>
                    <div ref={tableWrapRef} className={layoutStyles.tableWrap}>
                        <Table
                            scroll={{
                                x: 1400,
                                ...(tableScrollY != null ? { y: tableScrollY } : {}),
                            }}
                            className={`${Gstyle.table} ${styles.fileTable}`}
                            loading={loading}
                            columns={columns}
                            dataSource={dataSource}
                            rowKey={record => record.id}
                            pagination={{
                                total: total,
                                pageSize: pageSize,
                                current: current,
                                showTotal: total => `共 ${total} 条`,
                                showQuickJumper: true,
                                showSizeChanger: true,
                                onChange: (page, size) => {
                                    setCurrent(page);
                                    setPageSize(size);
                                },
                            }}
                            rowClassName={(record, index) => {
                                const isMultipleOfTwo = (index + 1) % 2 === 0;
                                return isMultipleOfTwo
                                    ? `${Gstyle.tableRow} wechat-table-row-stripe`
                                    : '';
                            }}
                        />
                    </div>
                </Content>
            </Layout>
            <Modal
                centered
                title={
                    <div className={Gstyle.modalTitle}>
                        <span>
                            {fileConfirmModal.mode === 'delete'
                                ? '确认删除'
                                : fileConfirmModal.invalidateUi === 'dateOnly'
                                    ? '确认生效日期'
                                    : '确认失效'}
                        </span>
                    </div>
                }
                closeIcon={<img src={closeIcon} alt="关闭" style={{ width: 20, height: 20 }} />}
                visible={fileConfirmModal.visible}
                onCancel={closeFileConfirmModal}
                width={410}
                footer={
                    <div className={Gstyle.custommodalfooter}>
                        <Button
                            className={Gstyle.mythemebutton}
                            ghost
                            onClick={closeFileConfirmModal}
                        >
                            取消
                        </Button>
                        <Button
                            type="primary"
                            loading={loading}
                            onClick={onConfirmFileActionModal}
                        >
                            {fileConfirmModal.mode === 'delete' ? '删除' : '确定'}
                        </Button>
                    </div>
                }
            >
                {fileConfirmModal.mode === 'delete' ? (
                    <p className={Gstyle.errorModalContent} >
                        {/* {`确定删除「${fileConfirmModal.record?.name ||
                            fileConfirmModal.record?.id ||
                            ''
                            }」吗？删除后不可恢复。`} */}
                        是否确认删除？
                    </p>
                ) : fileConfirmModal.invalidateUi === 'dateOnly' ? (
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div
                            style={{
                                color: '#000000',
                                fontSize: '14px',
                            }}
                        >
                            请选择有效期至
                        </div>
                        <DatePicker
                            style={{ flex: 1 }}
                            format="YYYY-MM-DD"
                            placeholder="年/月/日"
                            value={fileConfirmModal.invalidateExpireDate}
                            onChange={d =>
                                setFileConfirmModal(prev => ({
                                    ...prev,
                                    invalidateExpireDate: d,
                                }))
                            }
                            disabledDate={current =>
                                current &&
                                current < dayjs().startOf('day')
                            }
                        />
                    </div>
                ) : (
                    <p className={Gstyle.errorModalContent} >
                        是否确认失效？
                    </p>
                )}
            </Modal>
            <Modal
                centered
                title={
                    <div className={Gstyle.modalTitle}>
                        <span>{fileTitle}</span>
                    </div>
                }
                closeIcon={<img src={closeIcon} alt="关闭" style={{ width: 20, height: 20 }} />}
                visible={filevisible}
                onCancel={() => setFilevisible(false)}
                width={410}
                footer={
                    <div className={Gstyle.custommodalfooter}>
                        <Button
                            className={Gstyle.mythemebutton}
                            ghost
                            onClick={() => setFilevisible(false)}
                        >
                            取消
                        </Button>
                        <Button type="primary" loading={loading} onClick={onConfirmFolderModal}>
                            确定
                        </Button>
                    </div>
                }
            >
                {folderModalType === 'delete' ? (
                    <p className={Gstyle.errorModalContent} >
                        是否删除「{fileitem.name || ''}」文件夹?子文件夹及文件一同删除。
                    </p>
                ) : (
                    <TextArea
                        rows={3}
                        placeholder='请输入文件夹名称'
                        value={folderNameInput}
                        onChange={e => setFolderNameInput(e.target.value)}
                    />
                )}
            </Modal>
            <Modal
                centered
                title={
                    <div className={Gstyle.modalTitle}>
                        <span>{fileFormMode === 'add' ? '新增文件' : '编辑文件'}</span>
                    </div>
                }
                closeIcon={<img src={closeIcon} alt="关闭" style={{ width: 20, height: 20 }} />}
                visible={filevisibleType}
                onCancel={() => {
                    setUploadFileList([]);
                    setFilevisibleType(false);
                    form.resetFields();
                    resetFileFormTaTagState();
                }}
                width={950}
                footer={
                    <div className={Gstyle.custommodalfooters}>
                        <Button
                            className={Gstyle.mythemebutton}
                            ghost
                            onClick={() => {
                                setUploadFileList([]);
                                setFilevisibleType(false);
                                form.resetFields();
                                resetFileFormTaTagState();
                            }}
                        >
                            取消
                        </Button>
                        <Button type="primary" loading={loading} onClick={onConfirmFileModal}>
                            确定
                        </Button>
                    </div>
                }
            >
                <Form
                    form={form}
                    layout="horizontal"
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    labelAlign="left"
                >
                    {fileFormMode === 'edit' && (
                        <>
                            <Form.Item name="id" hidden>
                                <Input type="hidden" />
                            </Form.Item>
                            <Form.Item name="status" hidden>
                                <Input type="hidden" />
                            </Form.Item>
                        </>
                    )}
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="文件夹"
                                name="folderId"
                                rules={[{ required: true, message: '请选择文件夹' }]}
                            >
                                <TreeSelect
                                    showSearch
                                    treeDefaultExpandAll
                                    style={{ width: '100%' }}
                                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                                    placeholder="请选择文件夹"
                                    allowClear
                                    treeData={folderTreeSelectData}
                                    treeNodeFilterProp="title"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label="文件审批编号"
                                name="approvalCode"
                                rules={[{ required: true, message: '请输入文件审批编号' }]}
                            >
                                <Input placeholder="请输入文件审批编号" allowClear />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="BU"
                                name="bu"
                                rules={[{ required: true, message: '请选择BU' }]}
                            >
                                <Select
                                    placeholder="请选择BU"
                                    options={dictBuOptions}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    style={{ width: '100%' }}
                                    onChange={handleFileFormBuChange}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="TA TAG"
                                name="taTag"
                                rules={[
                                    {
                                        required: true,
                                        type: 'array',
                                        min: 1,
                                        message: '请选择TA标签',
                                    },
                                ]}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder={
                                        fileFormBuId ? '请选择TA标签' : '请先选择BU'
                                    }
                                    options={fileFormTaTagOptions}
                                    disabled={!fileFormBuId}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    style={{ width: '100%' }}
                                    maxTagCount="responsive"
                                />
                            </Form.Item>
                        </Col>


                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="有效期至"
                                name="expirationDate"
                                rules={[{ required: true, message: '请选择有效期' }]}
                            >
                                <DatePicker
                                    placeholder="年/月/日"
                                    style={{ width: '100%' }}
                                    format="YYYY-MM-DD"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={
                                    <span>
                                        <span
                                            style={{
                                                color: '#ff4d4f',
                                                marginRight: 4,
                                                fontFamily: 'SimSun, sans-serif',
                                            }}
                                        >
                                            *
                                        </span>
                                        上传文件
                                    </span>
                                }
                                extra="附件大小不超过50M"
                            >
                                <Form.Item
                                    name="fileId"
                                    noStyle
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                fileFormMode === 'add'
                                                    ? '请先选择文件并等待上传完成'
                                                    : '请保留已上传文件，或删除后重新上传新文件',
                                        },
                                    ]}
                                >
                                    <Input type="hidden" />
                                </Form.Item>
                                <Upload
                                    accept=".pptx,.ppt,.pdf,.doc,.docx,.xls,.xlsx,.MP4,.wmv,.png,.jpg,.jpeg,.bmp,.m4v,.zip"
                                    fileList={uploadFileList}
                                    onChange={({ fileList }) => {
                                        const nextList = fileList.map(file => {
                                            const baseName =
                                                file.response?.name ?? file.originName;
                                            const fileSize =
                                                file.response?.fileSize ?? file.fileSize;
                                            if (
                                                file.status === 'done' &&
                                                baseName &&
                                                fileSize != null &&
                                                fileSize !== ''
                                            ) {
                                                return {
                                                    ...file,
                                                    originName: baseName,
                                                    fileSize,
                                                    name: buildUploadFileDisplayName(
                                                        baseName,
                                                        fileSize,
                                                    ),
                                                };
                                            }
                                            return file;
                                        });
                                        setUploadFileList(nextList);
                                        if (!nextList.length) {
                                            form.setFieldsValue({
                                                fileId: undefined,
                                                name: undefined,
                                            });
                                        }
                                    }}
                                    maxCount={1}
                                    beforeUpload={file => {
                                        const max = 50 * 1024 * 1024;
                                        /** 扩展名统一为小写且不带点，与 `split('.').pop()` 结果一致 */
                                        const allowedExtensions = [
                                            'pptx',
                                            'ppt',
                                            'pdf',
                                            'doc',
                                            'docx',
                                            'xls',
                                            'xlsx',
                                            'mp4',
                                            'wmv',
                                            'png',
                                            'jpg',
                                            'windowHeight',
                                            'bmp',
                                            'm4v',
                                            'zip'
                                        ];
                                        const parts = file.name.split('.');
                                        const fileExtension =
                                            parts.length > 1 ? parts.pop().toLowerCase() : '';
                                        if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
                                            Modal.warning({
                                                title: '提示',
                                                content: '仅支持上传以下格式文件：pptx, ppt, pdf, doc, docx, xls, xlsx, mp4, wmv, png, jpg, jpeg, bmp, m4v,zip',
                                            });
                                            return Upload.LIST_IGNORE;
                                        }
                                        // if (file.size > max) {
                                        //     Modal.warning({
                                        //         title: '提示',
                                        //         content: '附件大小不超过50M',
                                        //     });
                                        // }
                                        return true;
                                    }}
                                    customRequest={async options => {
                                        const { file, onError, onSuccess } = options;
                                        try {
                                            const res = await knowledgeUpload(file);
                                            if (res.status === 200) {
                                                const item = res.data;
                                                if (item == null) {
                                                    Modal.warning({
                                                        title: '提示',
                                                        content:
                                                            '上传成功但未解析到文件 ，请检查接口返回字段',
                                                    });
                                                    onError(new Error('no file id'));
                                                    return;
                                                }
                                                form.setFieldsValue({
                                                    fileId: item.fileId,
                                                    name: item.name,
                                                });
                                                form.validateFields(['fileId']).catch(
                                                    () => { }
                                                );
                                                onSuccess(res.data, file);
                                            } else {
                                                onError(
                                                    new Error(res.message || '上传失败')
                                                );
                                            }
                                        } catch (err) {
                                            onError(err);
                                        }
                                    }}
                                >
                                    <Button type="primary" style={{ borderRadius: '4px' }}>
                                        选择文件
                                    </Button>
                                    <span
                                        className={styles.text}
                                        style={{ marginLeft: 8 }}
                                        hidden={uploadFileList.length > 0}
                                    >
                                        未选择文件
                                    </span>
                                </Upload>
                            </Form.Item>
                            <Form.Item name="name" hidden>
                                <Input type="hidden" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                labelCol={{ span: 3 }}
                                wrapperCol={{ span: 21 }}
                                label="文件简介"
                                name="descInfo"
                            >
                                <TextArea rows={4} placeholder="请输入文件简介" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
            <Modal
                centered
                zIndex={1001}
                closeIcon={<img src={closeIcon} alt="关闭" style={{ width: 20, height: 20 }} />}
                visible={errorModalVisible}
                onCancel={closeErrorModal}
                width={410}
                footer={
                    <div style={{ textAlign: 'center' }}>
                        <Button
                            type="primary"
                            loading={loading}
                            onClick={closeErrorModal}
                            style={{
                                width: '170px',
                                height: '40px',
                                borderRadius: '6px'
                            }}
                        >
                            确定
                        </Button>
                    </div>
                }
            >
                <p className={Gstyle.errorModalContent} style={{ marginTop: '30px' }}>
                    {errorModalContent}
                </p>
            </Modal>
        </>
    );
};
export default FileManagement;
